/**
 * Global Firebase Error Handler
 * 
 * Handles Firebase permission errors and token expiration by:
 * 1. Detecting permission/auth errors
 * 2. Attempting to refresh the Firebase Auth token
 * 3. Automatically logging out if refresh fails
 */

import { auth } from '@/firebase/config';
import { onAuthStateChanged, reload, getIdToken } from 'firebase/auth';

// Track if we're currently handling a session error to prevent loops
let isHandlingSessionError = false;
let authStateUnsubscribe: (() => void) | null = null;

/**
 * Check if an error is a Firebase permission/auth error
 */
export function isFirebaseAuthError(error: any): boolean {
  if (!error) return false;
  
  const errorCode = error.code || '';
  const errorMessage = error.message || '';
  
  // Check for permission/auth related error codes
  const isPermissionError = 
    errorCode === 'permission-denied' ||
    errorCode === 'unauthenticated' ||
    errorCode === 'auth/network-request-failed' ||
    errorMessage.includes('Missing or insufficient permissions') ||
    errorMessage.includes('permission') ||
    errorMessage.includes('unauthenticated') ||
    errorMessage.includes('session') ||
    errorMessage.includes('expired');
  
  return isPermissionError;
}

/**
 * Attempt to refresh the Firebase Auth token
 */
async function refreshAuthToken(): Promise<boolean> {
  if (!auth?.currentUser) {
    console.log('⚠️ No Firebase user found, cannot refresh token');
    return false;
  }

  try {
    console.log('🔄 Attempting to refresh Firebase Auth token...');
    
    // Reload the user to get fresh token
    await reload(auth.currentUser);
    
    // Get the ID token to force refresh
    const token = await getIdToken(auth.currentUser, true); // true = force refresh
    
    if (token) {
      console.log('✅ Firebase Auth token refreshed successfully');
      return true;
    } else {
      console.log('⚠️ Token refresh returned empty token');
      return false;
    }
  } catch (error: any) {
    console.error('❌ Failed to refresh Firebase Auth token:', error);
    return false;
  }
}

/**
 * Handle Firebase auth errors by attempting token refresh or logout
 */
export async function handleFirebaseAuthError(error: any): Promise<boolean> {
  // Prevent infinite loops
  if (isHandlingSessionError) {
    return false;
  }

  // Check if this is an auth error
  if (!isFirebaseAuthError(error)) {
    return false;
  }

  console.log('⚠️ Firebase auth/permission error detected:', {
    code: error.code,
    message: error.message,
  });

  isHandlingSessionError = true;

  try {
    // First, check if user is still authenticated in Firebase Auth
    if (!auth?.currentUser) {
      console.log('⚠️ No Firebase Auth user found, triggering logout');
      triggerLogout();
      return false;
    }

    // Attempt to refresh the token
    const refreshed = await refreshAuthToken();
    
    if (refreshed) {
      console.log('✅ Token refreshed, user should be able to continue');
      // Reset the flag after a delay to allow operations to retry
      setTimeout(() => {
        isHandlingSessionError = false;
      }, 2000);
      return true;
    } else {
      console.log('⚠️ Token refresh failed, triggering logout');
      triggerLogout();
      return false;
    }
  } catch (refreshError) {
    console.error('❌ Error during token refresh attempt:', refreshError);
    triggerLogout();
    return false;
  }
}

/**
 * Trigger automatic logout when session is invalid
 */
function triggerLogout() {
  console.log('🔄 Triggering automatic logout due to session expiration');
  
  // Dispatch event for App.tsx SessionTimeoutHandler to catch
  window.dispatchEvent(new CustomEvent('session_timeout'));
  
  // Also directly clear localStorage and redirect as fallback
  setTimeout(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('lastLogin');
    localStorage.removeItem('pendingAuditLog');
    
    // Redirect to login
    const { getLoginUrl } = require('@/utils/domainUtils');
    window.location.href = getLoginUrl();
  }, 100);
}

/**
 * Set up Firebase Auth state listener to monitor token expiration
 */
export function setupAuthStateListener() {
  if (!auth) {
    console.warn('⚠️ Firebase Auth not initialized, cannot set up auth state listener');
    return;
  }

  // Clean up existing listener if any
  if (authStateUnsubscribe) {
    authStateUnsubscribe();
  }

  console.log('🔐 Setting up Firebase Auth state listener...');
  
  authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      // User is authenticated, check token expiration
      try {
        const token = await getIdToken(user);
        if (token) {
          console.log('✅ Firebase Auth user is authenticated');
          
          // Initialize FCM for push notifications (non-blocking, retry if needed)
          // This ensures FCM initializes even if it was interrupted during login/navigation
          (async () => {
            try {
              const { initializeFCM } = await import('@/services/fcmService');
              await initializeFCM();
            } catch (error) {
              // Silently fail - FCM initialization is non-critical
              // It will be retried on next auth state change or page load
              console.warn('⚠️ FCM initialization failed in auth state listener:', error);
            }
          })();
        } else {
          console.warn('⚠️ Firebase Auth user exists but token is invalid');
        }
      } catch (error) {
        console.error('❌ Error checking Firebase Auth token:', error);
        // Token might be expired, but don't logout immediately
        // Let the error handler deal with it when operations fail
      }
    } else {
      console.log('⚠️ Firebase Auth user is null - user is not authenticated');
      // Don't logout immediately here, as user might be using OAuth fallback
      // Only logout when actual permission errors occur
    }
  });
}

/**
 * Clean up auth state listener
 */
export function cleanupAuthStateListener() {
  if (authStateUnsubscribe) {
    authStateUnsubscribe();
    authStateUnsubscribe = null;
  }
}

/**
 * Wrapper for Firebase operations that automatically handles auth errors
 */
export async function withAuthErrorHandling<T>(
  operation: () => Promise<T>,
  onError?: (error: any) => void
): Promise<T> {
  try {
    return await operation();
  } catch (error: any) {
    // Check if it's an auth error
    if (isFirebaseAuthError(error)) {
      // Attempt to handle it (refresh token or logout)
      await handleFirebaseAuthError(error);
      
      // Call custom error handler if provided
      if (onError) {
        onError(error);
      }
    } else {
      // Not an auth error, call custom error handler if provided
      if (onError) {
        onError(error);
      }
    }
    
    // Re-throw the error so calling code can handle it
    throw error;
  }
}


